import { sendEmailRequest } from '../services/emailDeliveryService';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SITE_CONFIG } from '../config';
import { getPositiveUserId, getStoredUser } from '../utils/authUser';
import './ChatLeadCampaignBuilder.css';

const COMM_BASE_URL = 'https://tophamtechbe.com/api/treas/comm';
const IDX_BASE_URL = 'https://tophamtechbe.com/api/treas/idx/v1';
const API_KEY = 'treas-api-key-2025';
const CAMPAIGN_KEY = 'chatlead-followup';
const MAX_STEPS = 5;
const MAX_DELAY_MINUTES = 525600;

const DELAY_UNITS = [
  { value: 'minutes', label: 'minutes', multiplier: 1 },
  { value: 'hours', label: 'hours', multiplier: 60 },
  { value: 'days', label: 'days', multiplier: 1440 },
  { value: 'weeks', label: 'weeks', multiplier: 10080 }
];

const PERSONALIZATION_VARIABLES = [
  { label: 'First name', value: '{{firstName}}' },
  { label: 'Area', value: '{{area}}' },
  { label: 'Service request', value: '{{serviceRequest}}' },
  { label: 'Email', value: '{{emailAddress}}' },
  { label: 'Postal code', value: '{{postalCode}}' }
];

const getApiMessage = async (response, fallback) => {
  try {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const data = await response.json();
      return data.message || data.code || fallback;
    }
    return (await response.text()) || fallback;
  } catch (error) {
    return fallback;
  }
};

const delayToFields = (delayMinutes = 0) => {
  const minutes = Number(delayMinutes) || 0;
  if (minutes === 0) return { timingMode: 'immediate', delayValue: '0', delayUnit: 'minutes' };

  const bestUnit = [...DELAY_UNITS]
    .reverse()
    .find(unit => minutes % unit.multiplier === 0);

  return {
    timingMode: 'delay',
    delayValue: String(minutes / (bestUnit?.multiplier || 1)),
    delayUnit: bestUnit?.value || 'minutes'
  };
};

const getDelayMinutes = (step) => {
  if (step.timingMode === 'immediate') return 0;
  const unit = DELAY_UNITS.find(option => option.value === step.delayUnit);
  return Number(step.delayValue) * (unit?.multiplier || 1);
};

const formatDelay = (step) => {
  const minutes = getDelayMinutes(step);
  if (minutes === 0) return 'Immediately';
  const value = Number(step.delayValue);
  const singular = step.delayUnit.replace(/s$/, '');
  return `${value} ${value === 1 ? singular : step.delayUnit} after lead arrives`;
};

const createEmptyStep = (index, previousDelay = 0) => {
  const delay = index === 0 ? 0 : Math.min(previousDelay + 1440, MAX_DELAY_MINUTES);
  return {
    localKey: `chatlead-step-${Date.now()}-${index}`,
    templateId: '',
    templateName: `chatlead-followup-${index + 1}`,
    subject: '',
    body: '',
    prompt: '',
    generating: false,
    templateDirty: false,
    editorOpen: true,
    previewMode: 'preview',
    ...delayToFields(delay)
  };
};

const normalizeScope = (scope) => String(scope || 'brokerage').toLowerCase();
const isValidEmailAddress = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const ChatLeadCampaignBuilder = ({ senderPassword = '' }) => {
  const currentUserId = getPositiveUserId(getStoredUser());
  const [templates, setTemplates] = useState([]);
  const [campaign, setCampaign] = useState(null);
  const [steps, setSteps] = useState([createEmptyStep(0)]);
  const [masterEnabled, setMasterEnabled] = useState(false);
  const [senderEmail, setSenderEmail] = useState('');
  const [senderConfigured, setSenderConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTestIndex, setSendingTestIndex] = useState(null);
  const [testRecipientEmail, setTestRecipientEmail] = useState('');
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const loadCampaign = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const headers = { 'X-API-Key': API_KEY, Accept: 'application/json' };
      const [flagsResponse, templatesResponse, campaignResponse] = await Promise.all([
        fetch(`${IDX_BASE_URL}/feature-flag?siteId=${encodeURIComponent(SITE_CONFIG.SITE_ID)}`, { headers }),
        fetch(`${COMM_BASE_URL}/templates?siteId=${encodeURIComponent(SITE_CONFIG.SITE_ID)}`, { headers }),
        fetch(`${COMM_BASE_URL}/drip-campaign-definitions?siteId=${encodeURIComponent(SITE_CONFIG.SITE_ID)}&campaignKey=${CAMPAIGN_KEY}`, { headers })
      ]);

      if (!flagsResponse.ok) {
        throw new Error(await getApiMessage(flagsResponse, 'Could not load automated follow-up settings.'));
      }
      if (!templatesResponse.ok) {
        throw new Error(await getApiMessage(templatesResponse, 'Could not load brokerage templates.'));
      }
      if (!campaignResponse.ok && campaignResponse.status !== 404) {
        throw new Error(await getApiMessage(campaignResponse, 'Could not load the chat-lead campaign.'));
      }

      const flags = await flagsResponse.json();
      const templateData = await templatesResponse.json();
      const brokerageTemplates = (Array.isArray(templateData) ? templateData : [])
        .filter(template => normalizeScope(template.scope) === 'brokerage')
        .sort((a, b) => String(a.templateName || '').localeCompare(String(b.templateName || '')));
      const campaignData = campaignResponse.status === 404 ? null : await campaignResponse.json();

      const loadedSteps = (campaignData?.steps || []).slice(0, MAX_STEPS).map((step, index) => {
        const template = brokerageTemplates.find(item => Number(item.id) === Number(step.templateId));
        return {
          localKey: `chatlead-step-${step.stepNumber || index + 1}-${step.templateId}`,
          templateId: String(step.templateId || ''),
          templateName: step.templateName || template?.templateName || '',
          subject: step.subject || template?.subject || '',
          body: template?.body || '',
          prompt: '',
          generating: false,
          templateDirty: false,
          editorOpen: index === 0,
          previewMode: 'preview',
          ...delayToFields(step.delayMinutes)
        };
      });

      setTemplates(brokerageTemplates);
      setCampaign(campaignData);
      setSteps(loadedSteps.length ? loadedSteps : [createEmptyStep(0)]);
      setMasterEnabled(Boolean(flags.automatedEmailFollowup));
      setSenderEmail(flags.senderEmail || '');
      setSenderConfigured(Boolean(flags.senderEmail && flags.senderEmailPassword));
      setDirty(false);
    } catch (loadError) {
      setError(loadError.message || 'Could not load the campaign builder.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCampaign();
  }, [loadCampaign]);

  useEffect(() => {
    const warnBeforeLeaving = (event) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeLeaving);
    return () => window.removeEventListener('beforeunload', warnBeforeLeaving);
  }, [dirty]);

  const status = useMemo(() => {
    if (masterEnabled && campaign?.active) return { label: 'Active', tone: 'active', detail: 'New chat leads are entering this sequence.' };
    if (masterEnabled && !campaign?.active) return { label: 'Needs attention', tone: 'warning', detail: 'The master switch is on, but the campaign is not active.' };
    return { label: 'Paused', tone: 'paused', detail: 'New chat leads are not entering this sequence.' };
  }, [campaign, masterEnabled]);

  const updateStep = (index, patch, templateChanged = false, markDirty = true) => {
    setSteps(current => current.map((step, stepIndex) => (
      stepIndex === index
        ? { ...step, ...patch, ...(templateChanged ? { templateDirty: true } : {}) }
        : step
    )));
    if (markDirty) {
      setDirty(true);
      setNotice('');
    }
  };

  const selectTemplate = (index, templateId) => {
    const template = templates.find(item => String(item.id) === String(templateId));
    updateStep(index, template ? {
      templateId: String(template.id),
      templateName: template.templateName || '',
      subject: template.subject || '',
      body: template.body || '',
      templateDirty: false,
      previewMode: 'preview'
    } : {
      templateId: '',
      templateName: `chatlead-followup-${index + 1}`,
      subject: '',
      body: '',
      templateDirty: false
    });
  };

  const addStep = () => {
    if (steps.length >= MAX_STEPS) return;
    const previousDelay = getDelayMinutes(steps[steps.length - 1]);
    setSteps(current => [...current, createEmptyStep(current.length, previousDelay)]);
    setDirty(true);
  };

  const removeStep = (index) => {
    if (steps.length === 1) return;
    setSteps(current => current.filter((step, stepIndex) => stepIndex !== index));
    setDirty(true);
  };

  const generateEmail = async (index) => {
    const step = steps[index];
    if (!step.prompt.trim()) {
      setError(`Describe what Email ${index + 1} should accomplish before generating it.`);
      return;
    }

    updateStep(index, { generating: true }, false, false);
    setError('');

    try {
      const request = [
        'Create a reusable brokerage email for an automated chat-lead follow-up campaign.',
        'Do not hard-code a recipient name. Use chat-lead personalization variables such as {{firstName}}, {{area}}, and {{serviceRequest}} when relevant.',
        'Do not add an unsubscribe footer because the delivery service appends it automatically.',
        `Email ${index + 1} goal: ${step.prompt.trim()}`
      ].join(' ');
      const response = await fetch(`${COMM_BASE_URL}/generate-ai-template`, {
        method: 'POST',
        headers: { 'X-API-Key': API_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: SITE_CONFIG.SITE_ID,
          request,
          generationMode: 'agent_marketing',
          researchMode: 'auto'
        })
      });

      if (!response.ok) {
        throw new Error(await getApiMessage(response, `Could not generate Email ${index + 1}.`));
      }
      const data = await response.json();
      if (!data.html) throw new Error('The AI generator did not return email content.');

      updateStep(index, {
        subject: data.subject || step.subject || `Chat lead follow-up ${index + 1}`,
        body: data.html,
        templateName: step.templateName || `chatlead-followup-${index + 1}`,
        templateDirty: true,
        generating: false,
        editorOpen: true,
        previewMode: 'preview'
      });
    } catch (generationError) {
      updateStep(index, { generating: false }, false, false);
      setError(generationError.message || `Could not generate Email ${index + 1}.`);
    }
  };

  const insertVariable = (index, value) => {
    const step = steps[index];
    const spacer = step.subject && !step.subject.endsWith(' ') ? ' ' : '';
    updateStep(index, { subject: `${step.subject}${spacer}${value}` }, true);
  };

  const personalizeTestContent = (content) => {
    const sampleValues = {
      firstName: 'Test Lead',
      area: SITE_CONFIG.AGENT?.OFFICE_CITY || 'your area',
      serviceRequest: 'buying a home',
      emailAddress: testRecipientEmail.trim(),
      postalCode: '12345'
    };

    return Object.entries(sampleValues).reduce(
      (personalized, [key, value]) => personalized.replaceAll(`{{${key}}}`, value),
      content
    );
  };

  const sendTestEmail = async (index) => {
    const step = steps[index];
    const recipientEmail = testRecipientEmail.trim();
    if (!senderEmail || !senderPassword) {
      setError('Connect a sender mailbox before sending a test email.');
      return;
    }
    if (!isValidEmailAddress(recipientEmail)) {
      setError('Enter a valid recipient email address before sending a test.');
      return;
    }
    if (!step?.subject.trim() || !step?.body.trim()) {
      setError(`Email ${index + 1} needs a subject and email content before you can send a test.`);
      return;
    }

    setSendingTestIndex(index);
    setError('');
    setNotice('');

    const formData = new FormData();
    formData.append('emailaddress', recipientEmail);
    formData.append('title', personalizeTestContent(step.subject.trim()));
    formData.append('body', personalizeTestContent(step.body));
    formData.append('template', step.templateName.trim() || `chatlead-followup-${index + 1}`);
    formData.append('email', senderEmail);
    formData.append('emailPassword', senderPassword);

    try {
      const response = await sendEmailRequest(`${COMM_BASE_URL}/send-email`, {
        method: 'POST',
        headers: { 'X-API-Key': API_KEY },
        body: formData
      });
      if (!response.ok) {
        throw new Error(await getApiMessage(response, `Could not send the test for Email ${index + 1}.`));
      }
      setNotice(`Test for Email ${index + 1} sent to ${recipientEmail}.`);
    } catch (sendError) {
      setError(sendError.message || `Could not send the test for Email ${index + 1}.`);
    } finally {
      setSendingTestIndex(null);
    }
  };

  const validateCampaign = (requireSender) => {
    if (!steps.length) return 'Add at least one email to the campaign.';
    if (steps.length > MAX_STEPS) return `Chat-lead campaigns can contain up to ${MAX_STEPS} emails.`;
    if (requireSender && !senderConfigured) return 'Connect a sender email before turning on automated follow-up.';

    let previousDelay = -1;
    for (let index = 0; index < steps.length; index += 1) {
      const step = steps[index];
      const delayMinutes = getDelayMinutes(step);
      if (!step.templateName.trim() || !step.subject.trim() || !step.body.trim()) {
        return `Email ${index + 1} needs a template name, subject, and email content.`;
      }
      if (!Number.isInteger(delayMinutes) || delayMinutes < 0) {
        return `Email ${index + 1} needs a whole-number delay.`;
      }
      if (delayMinutes > MAX_DELAY_MINUTES) {
        return `Email ${index + 1} cannot be scheduled more than one year after the lead arrives.`;
      }
      if (delayMinutes <= previousDelay) {
        return `Email ${index + 1} must send after Email ${index}.`;
      }
      previousDelay = delayMinutes;
    }
    return '';
  };

  const persistTemplates = async () => {
    for (let index = 0; index < steps.length; index += 1) {
      const step = steps[index];
      if (step.templateId && !step.templateDirty) continue;

      const form = new URLSearchParams({
        templateName: step.templateName.trim(),
        subject: step.subject.trim(),
        body: step.body,
        siteId: SITE_CONFIG.SITE_ID,
        scope: 'BROKERAGE'
      });
      if (currentUserId) form.set('createdByUserId', String(currentUserId));

      const response = await fetch(`${COMM_BASE_URL}/store-template`, {
        method: 'POST',
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
        },
        body: form.toString()
      });
      if (!response.ok) {
        throw new Error(await getApiMessage(response, `Could not save the template for Email ${index + 1}.`));
      }
    }

    const response = await fetch(`${COMM_BASE_URL}/templates?siteId=${encodeURIComponent(SITE_CONFIG.SITE_ID)}`, {
      headers: { 'X-API-Key': API_KEY, Accept: 'application/json' }
    });
    if (!response.ok) throw new Error(await getApiMessage(response, 'Templates saved, but their IDs could not be reloaded.'));
    const data = await response.json();
    return (Array.isArray(data) ? data : []).filter(template => normalizeScope(template.scope) === 'brokerage');
  };

  const updateMasterSwitch = async (enabled) => {
    const response = await fetch(
      `${IDX_BASE_URL}/feature-flag?siteId=${encodeURIComponent(SITE_CONFIG.SITE_ID)}&automatedEmailFollowup=${enabled}`,
      { method: 'POST', headers: { 'X-API-Key': API_KEY } }
    );
    if (!response.ok) {
      throw new Error(await getApiMessage(response, `The campaign was saved, but automated follow-up could not be ${enabled ? 'enabled' : 'disabled'}.`));
    }
  };

  const saveCampaign = async ({ enableAfterSave }) => {
    const validationMessage = validateCampaign(enableAfterSave || masterEnabled);
    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    setSaving(true);
    setError('');
    setNotice('');

    try {
      const refreshedTemplates = await persistTemplates();
      const payloadSteps = steps.map((step, index) => {
        const match = refreshedTemplates.find(template => (
          String(template.templateName || '').trim() === step.templateName.trim()
          && normalizeScope(template.scope) === 'brokerage'
        ));
        const templateId = match?.id || step.templateId;
        if (!templateId) throw new Error(`Could not find the saved template for Email ${index + 1}.`);
        return {
          stepNumber: index + 1,
          templateId: Number(templateId),
          delayMinutes: getDelayMinutes(step)
        };
      });

      const campaignResponse = await fetch(`${COMM_BASE_URL}/drip-campaign-definitions/${CAMPAIGN_KEY}`, {
        method: 'PUT',
        headers: { 'X-API-Key': API_KEY, 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ siteId: SITE_CONFIG.SITE_ID, active: true, steps: payloadSteps })
      });
      if (!campaignResponse.ok) {
        const fallback = campaignResponse.status === 409
          ? 'Someone else updated this campaign. Reload it before saving again.'
          : 'Could not save the chat-lead campaign.';
        throw new Error(await getApiMessage(campaignResponse, fallback));
      }

      if (enableAfterSave && !masterEnabled) await updateMasterSwitch(true);
      setNotice(enableAfterSave ? 'Chat-lead follow-up is active.' : 'Campaign changes saved. Automated follow-up remains paused.');
      await loadCampaign();
    } catch (saveError) {
      setError(saveError.message || 'Could not save the campaign.');
    } finally {
      setSaving(false);
    }
  };

  const pauseCampaign = async () => {
    setSaving(true);
    setError('');
    setNotice('');

    try {
      await updateMasterSwitch(false);

      if (campaign) {
        const response = await fetch(`${COMM_BASE_URL}/drip-campaign-definitions/${CAMPAIGN_KEY}`, {
          method: 'PUT',
          headers: { 'X-API-Key': API_KEY, 'Content-Type': 'application/json' },
          body: JSON.stringify({ siteId: SITE_CONFIG.SITE_ID, active: false })
        });
        if (!response.ok && response.status !== 404) {
          setNotice('Automated follow-up is safely paused, but the campaign definition could not be deactivated.');
        } else {
          setNotice('Chat-lead follow-up is paused.');
        }
      } else {
        setNotice('Chat-lead follow-up is paused.');
      }
      await loadCampaign();
    } catch (pauseError) {
      setError(pauseError.message || 'Could not pause automated follow-up.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="chatlead-automation-loading">Loading chat-lead automation…</div>;
  }

  return (
    <section className="chatlead-automation" aria-labelledby="chatlead-automation-title">
      <header className="chatlead-automation-hero">
        <div>
          <div className="chatlead-automation-kicker">Chat lead automation</div>
          <h2 id="chatlead-automation-title">Follow up while interest is fresh</h2>
          <p>Every new chat lead with an email address can enter this brokerage-wide sequence automatically.</p>
        </div>
        <div className="chatlead-automation-status-wrap">
          <span className={`chatlead-automation-status ${status.tone}`}><i />{status.label}</span>
          <small>{status.detail}</small>
        </div>
      </header>

      <div className="chatlead-automation-readiness">
        <div className={senderConfigured ? 'ready' : 'missing'}>
          <span aria-hidden="true">{senderConfigured ? '✓' : '!'}</span>
          <div>
            <strong>{senderConfigured ? 'Sender connected' : 'Sender email required'}</strong>
            <small>{senderConfigured ? senderEmail : 'Configure a sender mailbox in Email Manager before turning this on.'}</small>
          </div>
        </div>
        <div className="ready">
          <span aria-hidden="true">{steps.length}</span>
          <div><strong>{steps.length} of {MAX_STEPS} emails</strong><small>Applies to new chat leads after publishing.</small></div>
        </div>
        {campaign?.campaignVersion && (
          <div className="ready">
            <span aria-hidden="true">v{campaign.campaignVersion}</span>
            <div><strong>Campaign version</strong><small>Publishing changes creates the next saved configuration.</small></div>
          </div>
        )}
      </div>

      {error && <div className="chatlead-automation-message error" role="alert">{error}</div>}
      {notice && <div className="chatlead-automation-message success" role="status">{notice}</div>}
      {dirty && <div className="chatlead-automation-unsaved">Unsaved changes</div>}

      <div className="chatlead-sequence" aria-label="Campaign email sequence">
        {steps.map((step, index) => (
          <article className="chatlead-step" key={step.localKey}>
            <div className="chatlead-step-rail" aria-hidden="true">
              <span>{index + 1}</span>
              {index < steps.length - 1 && <i />}
            </div>
            <div className="chatlead-step-card">
              <button
                type="button"
                className="chatlead-step-summary"
                onClick={() => updateStep(index, { editorOpen: !step.editorOpen }, false, false)}
                aria-expanded={step.editorOpen}
              >
                <span>
                  <small>Email {index + 1} · {formatDelay(step)}</small>
                  <strong>{step.subject || 'Choose or generate an email template'}</strong>
                </span>
                <b>{step.editorOpen ? '−' : '+'}</b>
              </button>

              {step.editorOpen && (
                <div className="chatlead-step-editor">
                  <div className="chatlead-step-fields two-column">
                    <label>
                      <span>Brokerage template</span>
                      <select value={step.templateId} onChange={event => selectTemplate(index, event.target.value)}>
                        <option value="">Create a new campaign template</option>
                        {templates.map(template => (
                          <option key={template.id} value={template.id}>{template.templateName}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>Send timing</span>
                      <div className={`chatlead-delay-control ${index > 0 ? 'without-mode' : ''}`}>
                        {index === 0 && (
                          <select
                            value={step.timingMode}
                            onChange={event => updateStep(index, {
                              timingMode: event.target.value,
                              ...(event.target.value === 'immediate' ? { delayValue: '0', delayUnit: 'minutes' } : { delayValue: '1', delayUnit: 'hours' })
                            })}
                          >
                            <option value="immediate">Immediately</option>
                            <option value="delay">After</option>
                          </select>
                        )}
                        {(index > 0 || step.timingMode === 'delay') && (
                          <>
                            <input
                              type="number"
                              min="1"
                              step="1"
                              value={step.delayValue}
                              onChange={event => updateStep(index, { timingMode: 'delay', delayValue: event.target.value })}
                              aria-label={`Email ${index + 1} delay`}
                            />
                            <select value={step.delayUnit} onChange={event => updateStep(index, { delayUnit: event.target.value })}>
                              {DELAY_UNITS.map(unit => <option key={unit.value} value={unit.value}>{unit.label}</option>)}
                            </select>
                          </>
                        )}
                      </div>
                    </label>
                  </div>

                  <div className="chatlead-ai-row">
                    <label>
                      <span>Ask AI to write or revise this email</span>
                      <textarea
                        rows="2"
                        value={step.prompt}
                        onChange={event => updateStep(index, { prompt: event.target.value })}
                        placeholder="Example: Thank them for reaching out, acknowledge their preferred area, and invite them to reply with questions."
                      />
                    </label>
                    <button type="button" onClick={() => generateEmail(index)} disabled={step.generating || !step.prompt.trim()}>
                      {step.generating ? 'Generating…' : step.body ? 'Regenerate with AI' : 'Generate with AI'}
                    </button>
                  </div>

                  <div className="chatlead-step-fields">
                    <label>
                      <span>Template name <em>Brokerage-wide</em></span>
                      <input value={step.templateName} onChange={event => updateStep(index, { templateName: event.target.value }, true)} />
                    </label>
                    <label>
                      <span>Subject</span>
                      <input value={step.subject} onChange={event => updateStep(index, { subject: event.target.value }, true)} placeholder="Email subject" />
                    </label>
                  </div>

                  <div className="chatlead-variable-row" aria-label="Personalization variables">
                    <span>Insert into subject:</span>
                    {PERSONALIZATION_VARIABLES.map(variable => (
                      <button type="button" key={variable.value} onClick={() => insertVariable(index, variable.value)}>{variable.label}</button>
                    ))}
                  </div>

                  <div className="chatlead-preview-tabs">
                    <button type="button" className={step.previewMode === 'preview' ? 'active' : ''} onClick={() => updateStep(index, { previewMode: 'preview' }, false, false)}>Preview</button>
                    <button type="button" className={step.previewMode === 'html' ? 'active' : ''} onClick={() => updateStep(index, { previewMode: 'html' }, false, false)}>Edit HTML</button>
                  </div>
                  {step.previewMode === 'preview' ? (
                    step.body ? <iframe title={`Email ${index + 1} preview`} srcDoc={step.body} sandbox="" /> : <div className="chatlead-empty-preview">Choose a template or generate this email with AI to preview it.</div>
                  ) : (
                    <textarea
                      className="chatlead-html-editor"
                      rows="14"
                      value={step.body}
                      onChange={event => updateStep(index, { body: event.target.value }, true)}
                      placeholder="Email HTML / Thymeleaf content"
                    />
                  )}

                  <div className="chatlead-step-footer">
                    <small>Tests use sample lead details. The unsubscribe link and required delivery headers are added automatically.</small>
                    <div className="chatlead-step-footer-actions">
                      <input
                        type="email"
                        className="chatlead-test-recipient"
                        value={testRecipientEmail}
                        onChange={event => setTestRecipientEmail(event.target.value)}
                        onKeyDown={event => {
                          if (event.key === 'Enter' && isValidEmailAddress(testRecipientEmail) && sendingTestIndex === null) {
                            event.preventDefault();
                            sendTestEmail(index);
                          }
                        }}
                        placeholder="test@example.com"
                        aria-label={`Recipient for Email ${index + 1} test`}
                        disabled={sendingTestIndex !== null}
                      />
                      <button
                        type="button"
                        className="send-test"
                        onClick={() => sendTestEmail(index)}
                        disabled={sendingTestIndex !== null || !senderConfigured || !senderPassword || !isValidEmailAddress(testRecipientEmail) || !step.subject.trim() || !step.body.trim()}
                        title={senderConfigured && senderPassword ? 'Send this email to the address entered here' : 'Connect a sender mailbox to send a test'}
                      >
                        {sendingTestIndex === index ? 'Sending test…' : 'Send test'}
                      </button>
                      <button type="button" className="remove" onClick={() => removeStep(index)} disabled={steps.length === 1}>Remove email</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </article>
        ))}
      </div>

      <button type="button" className="chatlead-add-step" onClick={addStep} disabled={steps.length >= MAX_STEPS}>
        + Add email {steps.length >= MAX_STEPS && `(maximum ${MAX_STEPS})`}
      </button>

      <footer className="chatlead-automation-actions">
        <div>
          <strong>{status.label}</strong>
          <small>{masterEnabled ? 'Saving publishes a new campaign configuration.' : 'You can save changes without allowing new enrollments.'}</small>
        </div>
        <div>
          {masterEnabled && <button type="button" className="pause" onClick={pauseCampaign} disabled={saving}>Pause automation</button>}
          {!masterEnabled && campaign && (
            <button type="button" className="secondary" onClick={() => saveCampaign({ enableAfterSave: false })} disabled={saving || !dirty}>Save while paused</button>
          )}
          <button
            type="button"
            className="primary"
            onClick={() => saveCampaign({ enableAfterSave: !masterEnabled })}
            disabled={saving || (masterEnabled && campaign?.active && !dirty)}
          >
            {saving ? 'Saving…' : masterEnabled ? 'Save changes' : 'Save & turn on'}
          </button>
        </div>
      </footer>
    </section>
  );
};

export default ChatLeadCampaignBuilder;
